import { css } from '@emotion/react'
import styled from '@emotion/styled'
import React, { ButtonHTMLAttributes, useCallback, ElementType } from 'react'

import { pushButtonClick } from '@emico/datalayer'
import { overrideOptional } from '@emico/overrides'
import { theme } from '@emico/styles'
import { testId } from '@emico/utils'

import Loader from './Loader'

export interface Props extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Tracking event name.
   * Pass an empty string to disable tracking.
   * Note that, for this to work, analyticsContext needs to be set to an empty string too
   */
  analyticsName: string
  /**
   * Tracking event category.
   * Pass an empty string to disable tracking.
   * Note that, for this to work, analyticsName needs to be set to an empty string too
   */
  analyticsContext: string

  /** Set button to loading state */
  loading?: boolean

  /** Set the button to a disabled state */
  disabled?: boolean

  /** Content of the button, required because a button must have content to be accessible  */
  children: React.ReactNode

  onClick?(e: React.MouseEvent<HTMLElement, MouseEvent>): void

  /** Allow overriding the button with another component, for example, Link */
  as?: ElementType
}

export const StyledButton = overrideOptional(styled('button', {
  shouldForwardProp: (prop) => prop !== 'loading' && prop !== 'as',
})<{
  loading?: boolean
}>`
  padding: 0.25em 1em;
  font-family: inherit;
  border: none;
  font-weight: bold;

  &:focus {
    outline: none;
  }

  &:disabled {
    cursor: not-allowed;
  }

  ${(props) =>
    props.loading &&
    css`
      color: transparent;
    `}
`)

export const ButtonLoader = overrideOptional(styled(Loader)`
  width: 1.5em;
  height: 1.5em;
  margin-bottom: -1.5em;

  /* Loader border styles */
  border-color: ${theme.onPrimaryColor};
  border-left-color: transparent;
  border-width: 2px;
`)

const isAnalyticsEnabled =
  process.env.REACT_APP_DISABLE_ANALYTICS_BUTTON_CLICK !== 'true'

export function useHandleClick(
  analyticsName: Props['analyticsName'],
  analyticsContext: Props['analyticsContext'],
  onClick: Props['onClick'],
) {
  return useCallback(
    (e) => {
      // Allow for tracking to be disabled explicitly
      // Users should be allowed to pass only analytics name or category
      if (analyticsName !== '' || analyticsContext !== '') {
        pushButtonClick(analyticsName, analyticsContext)
      }

      if (onClick) {
        onClick(e)
      }
    },
    [analyticsContext, analyticsName, onClick],
  )
}

const Button = ({
  analyticsName,
  analyticsContext,
  onClick,
  children,
  loading,
  disabled,
  ...props
}: Props) => {
  const handleClick = useHandleClick(analyticsName, analyticsContext, onClick)

  const loader = loading ? <ButtonLoader /> : null

  return (
    <StyledButton
      disabled={disabled}
      loading={loading}
      onClick={isAnalyticsEnabled ? handleClick : onClick}
      data-testid={testId(analyticsName, analyticsContext)}
      {...props}
    >
      {loader}
      {children}
    </StyledButton>
  )
}

export default overrideOptional(Button)
